import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { TIPO_DOC_OFICINA } from "@/lib/constants";
import { fechaHoraCompleta } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SectionCard } from "@/components/marca/Layout";
import AuthImage from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { FileText, Download, Check, TriangleAlert, Upload, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const FORMATOS = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function EstadoDoc({ estado }) {
  if (estado === "recibido") return <span className="inline-flex items-center gap-1 rounded-full border border-ok-line bg-ok-soft px-2.5 py-[2px] text-[12.5px] font-semibold text-ok-ink"><Check className="h-3.5 w-3.5" />{t(" Recibido")}</span>;
  if (estado === "pendiente") return <span className="inline-flex items-center gap-1 rounded-full border border-[#E8B9B8] bg-brand-tint px-2.5 py-[2px] text-[12.5px] font-semibold text-danger"><TriangleAlert className="h-3.5 w-3.5" />{t(" Pendiente")}</span>;
  return <span className="inline-flex items-center rounded-full border border-line-strong bg-surface-2 px-2.5 py-[2px] text-[12.5px] font-semibold text-ink-2">{t("Opcional")}</span>;
}

function Chip({ ok, label }) {
  return <span className={cn("text-[12px] px-1.5 py-0.5 rounded", ok ? "bg-ok-soft text-ok-ink" : "bg-brand-tint text-danger")}>{ok ? "✓" : "✕"} {label}</span>;
}

function ArchivoFila({ a, onVer }) {
  const esc = a.escaneo;
  const rev = esc?.revision;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-line bg-surface-2 p-2.5" data-testid={`archivo-${a.id}`}>
      {a.mime?.startsWith("image/")
        ? <AuthImage fileId={a.file_id} className="h-14 w-14 shrink-0 rounded object-cover border" />
        : <span className="h-14 w-14 shrink-0 rounded border bg-white flex items-center justify-center"><FileText className="h-6 w-6 text-ink-3" /></span>}
      <div className="min-w-0 flex-1 text-[13.5px]">
        <p className="font-semibold text-ink truncate">{a.nombre}</p>
        <p className="text-ink-3">{a.origen === "oficina" ? t("Oficina · {v0}", { v0: a.subido_por || "dispatch" }) : t("Chofer · {v0}", { v0: a.chofer_nombre || "—" })} · {fechaHoraCompleta(a.subido_at)}</p>
        {esc && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {esc.aprobado_ia === true && <span className="text-[12px] font-bold text-ok flex items-center gap-1"><Check className="h-3 w-3" />{t(" Aprobado")}</span>}
            {esc.aprobado_ia === false && <span className="text-[12px] font-bold text-[#8A5A0B] flex items-center gap-1"><TriangleAlert className="h-3 w-3" />{t(" Con observaciones")}</span>}
            {esc.legibilidad != null && <span className="text-[12px] text-ink-2">{t("Legibilidad {legibilidad}/5", { legibilidad: esc.legibilidad })}</span>}
            {rev && <>
              <Chip ok={rev.tiene_firma} label={t("Firma")} />
              <Chip ok={rev.tiene_fecha} label={t("Fecha")} />
              <Chip ok={rev.tiene_nombre} label={t("Nombre")} />
              <Chip ok={!rev.borrosa} label={t("Nítida")} />
              <Chip ok={!rev.obstruccion} label={t("Sin obstrucción")} />
            </>}
            {esc.motivo_subida_forzada && <span className="text-[12px] text-[#8A5A0B]">{t("Subido igual: {motivo_subida_forzada}", { motivo_subida_forzada: esc.motivo_subida_forzada })}</span>}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Button type="button" size="sm" variant="outline" className="h-8" data-testid={`archivo-ver-${a.id}`} onClick={() => onVer(a.file_id)}>{t("Ver")}</Button>
        {a.original_file_id && <button type="button" data-testid={`ver-original-${a.file_id}`} onClick={() => onVer(a.original_file_id)} className="text-xs text-ink underline">{t("Ver original")}</button>}
      </div>
    </div>
  );
}

// Fix P (2026-10-13): documentos de la carga por etapa, con quién los subió y a qué hora real. Subirlos tarde no cambia las horas del movimiento.
export function DocumentosEtapas({ carga, onCambio, abrirDoc, soloLectura }) {
  const [subiendo, setSubiendo] = useState("");
  const [tipoNuevo, setTipoNuevo] = useState("otro");
  const archivos = carga.archivos || [];
  const nombreEtapa = (clave) => (carga.etapas || []).find((e) => e.clave === clave)?.nombre || clave;
  const hayPod = archivos.some((a) => a.tipo === "signed_pod");

  const post = async (url, fd, clave) => {
    setSubiendo(clave);
    try {
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Documento adjuntado"));
      await onCambio();
    } catch (err) { toast.error(apiError(err)); } finally { setSubiendo(""); }
  };

  const adjuntarEtapa = (d, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    fd.append("etapa", d.etapa);
    fd.append("tipo", d.tipo);
    files.forEach((f) => fd.append("files", f));
    post(`/cargas/${carga.id}/archivos`, fd, `${d.etapa}-${d.tipo}`);
  };

  const adjuntarOficina = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    fd.append("tipo", tipoNuevo);
    files.forEach((f) => fd.append("files", f));
    post(`/cargas/${carga.id}/documentos`, fd, "oficina");
  };

  const cambiarTipo = async (fileId, tipo) => {
    try {
      await api.put(`/cargas/${carga.id}/documentos/${fileId}`, { tipo });
      toast.success(t("Tipo de documento actualizado"));
      await onCambio();
    } catch (err) { toast.error(apiError(err)); }
  };

  const descargarPdf = async () => {
    try {
      const res = await api.get(`/cargas/${carga.id}/papeles-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = t("papeles_{numero_contenedor}.pdf", { numero_contenedor: carga.numero_contenedor });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const otros = archivos.filter((a) => a.tipo === "otro");

  return (
    <SectionCard
      titulo={t("Documentos por etapa e historial")}
      descripcion={t("Cada archivo queda con su etapa, su chofer y la fecha y hora de subida. Los documentos tardíos no cambian las horas del movimiento.")}
      acciones={hayPod && <Button data-testid="detalle-descargar-pdf-btn" size="sm" variant="outline" className="h-8" onClick={descargarPdf}><Download className="h-3.5 w-3.5 mr-1.5" />{t(" Descargar PDF")}</Button>}
      data-testid="documentos-etapas"
    >
      <div className="space-y-4">
        {(carga.docs || []).map((d) => {
          const lista = archivos.filter((a) => a.etapa === d.etapa && a.tipo === d.tipo);
          const clave = `${d.etapa}-${d.tipo}`;
          return (
            <div key={clave} data-testid={`doc-${clave}`} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[15px] text-ink"><span className="font-semibold">{d.etapa_nombre}</span> · {d.nombre} {d.requerido ? t("requerido") : t("opcional")}</p>
                <div className="flex items-center gap-2">
                  <span data-testid={`doc-estado-${clave}`}><EstadoDoc estado={d.estado} /></span>
                  {!soloLectura && (
                    <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line-strong bg-white text-[13px] font-semibold text-ink hover:bg-surface-2 cursor-pointer">
                      {subiendo === clave ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}{t(" Adjuntar")}<input data-testid={`doc-adjuntar-${clave}`} type="file" accept={d.tipo === "container_photo" ? ".jpg,.jpeg,.png,image/jpeg,image/png" : FORMATOS} multiple className="hidden" onChange={(e) => adjuntarEtapa(d, e)} />
                    </label>
                  )}
                </div>
              </div>
              {lista.map((a) => <ArchivoFila key={a.id} a={a} onVer={abrirDoc} />)}
            </div>
          );
        })}
        {(carga.docs || []).length === 0 && <p className="text-sm text-ink-3">{t("Esta carga no tiene documentos de etapa.")}</p>}

        {otros.length > 0 && (
          <div className="space-y-2" data-testid="archivos-otros">
            <p className="eyebrow">{t("Otros archivos de las etapas")}</p>
            {otros.map((a) => (
              <div key={a.id}>
                <p className="text-[13px] text-ink-2 mb-1">{nombreEtapa(a.etapa)}</p>
                <ArchivoFila a={a} onVer={abrirDoc} />
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 border-t border-line space-y-2" data-testid="documentos-oficina">
          <p className="eyebrow">{t("Documentos de la oficina (el chofer los ve en su app)")}</p>
          {(carga.documentos || []).length === 0 && <p className="text-sm text-ink-3">{t("Sin documentos de la oficina.")}</p>}
          {(carga.documentos || []).map((d) => (
            <div key={d.file_id} data-testid={`detalle-doc-${d.file_id}`} className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface-2 p-2.5">
              <button type="button" onClick={() => abrirDoc(d.file_id)} className="flex items-center gap-2 text-sm font-medium text-ink min-w-0 flex-1 text-left"><FileText className="h-4 w-4 text-ink-3 shrink-0" /><span className="truncate">{d.nombre}</span></button>
              <span className="text-xs text-ink-3">{d.subido_por}</span>
              <Select value={d.tipo || "otro"} onValueChange={(v) => cambiarTipo(d.file_id, v)} disabled={soloLectura}>
                <SelectTrigger data-testid={`doc-tipo-${d.file_id}`} className="h-8 w-44 bg-white text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(TIPO_DOC_OFICINA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          {!soloLectura && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Select value={tipoNuevo} onValueChange={setTipoNuevo}>
                <SelectTrigger data-testid="doc-tipo-nuevo" className="h-9 w-44 bg-white text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(TIPO_DOC_OFICINA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-line-strong bg-white text-[13px] font-semibold text-ink hover:bg-surface-2 cursor-pointer">
                {subiendo === "oficina" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{t(" Subir documento")}<input data-testid="doc-subir-oficina" type="file" accept={FORMATOS} multiple className="hidden" onChange={adjuntarOficina} />
              </label>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
